import { Badge } from "@arcadeai/design-system/components/ui/atoms/badge";
import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { Input } from "@arcadeai/design-system/components/ui/atoms/input";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@arcadeai/design-system/rhf";
import { Eye, EyeOff } from "lucide-react";
import { useState } from "react";
import { useFormContext } from "react-hook-form";
import type { AuthProvider } from "@/app/oauth/types";

type Props = {
  oauth?: {
    oauth2: { client_secret: { exists?: boolean } };
    binding?: { type: string };
  };
};

export const ClientCredentialsFields = ({ oauth }: Props = {}) => {
  const form = useFormContext<AuthProvider>();
  const [showPassword, setShowPassword] = useState(false);
  const isEditing = !!oauth;
  const canUpdate = oauth?.binding?.type !== "static";
  const clientSecretExists = oauth?.oauth2?.client_secret?.exists ?? false;

  const getClientSecretPlaceholder = () => {
    if (!isEditing) {
      return "Enter client secret";
    }
    if (canUpdate) {
      return "Leave empty to keep current, or enter new secret to replace";
    }
    return "Client secret is configured and cannot be modified";
  };

  return (
    <>
      <FormField
        control={form.control}
        name="oauth2.client_id"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Client ID</FormLabel>
            <FormControl>
              <Input
                {...field}
                disabled={!canUpdate}
                placeholder={
                  !canUpdate && isEditing
                    ? "Client ID is configured and cannot be modified"
                    : "Enter client ID"
                }
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="oauth2.client_secret"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="flex items-center gap-2">
              <span>Client Secret</span>
              {!isEditing && <span className="text-destructive">*</span>}
              {!!(isEditing && canUpdate) && (
                <Badge className="text-xs" variant="outline">
                  Optional - Leave empty to keep current
                </Badge>
              )}
            </FormLabel>
            {!!(isEditing && clientSecretExists) && (
              <div className="flex items-center justify-between rounded border-2 border-amber-200/60 bg-amber-50/30 p-2 dark:border-amber-800/40 dark:bg-amber-950/20">
                <div className="flex items-center gap-2">
                  <div className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                  <span className="font-medium text-xs">A secret is set</span>
                </div>
              </div>
            )}

            <FormControl>
              <div className="relative">
                <Input
                  autoComplete="client-secret"
                  disabled={!canUpdate}
                  placeholder={getClientSecretPlaceholder()}
                  type={showPassword ? "text" : "password"}
                  {...field}
                />
                <Button
                  className="absolute top-1/2 right-2 h-auto -translate-y-1/2 px-2 py-1"
                  disabled={!canUpdate}
                  onClick={() => setShowPassword(!showPassword)}
                  size="sm"
                  type="button"
                  variant="ghost"
                >
                  {showPassword ? (
                    <EyeOff className="size-4" />
                  ) : (
                    <Eye className="size-4" />
                  )}
                </Button>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  );
};
